<template>
    <v-card v-if="request">
        <v-row class="pt-2">
            <v-col>
                <v-label class="ml-4">Sample Processing Request</v-label>
                <v-card-title>{{ request.code }}</v-card-title>
                <v-card-subtitle>{{ request.experimentCode }} - {{ request.id }}</v-card-subtitle>
            </v-col>
        </v-row>
        <v-card-text>
            <v-tabs v-model="tab" bg-color="info" theme="dark">
                <v-tab value="Details">Details</v-tab>
            </v-tabs>
            <v-card>
                <v-card-text>
                    <v-card>
                        <v-card-title>
                            <v-row no-gutters>
                                Request Information
                                <v-spacer/>
                                <v-btn v-if="!isEditingRequest" variant="elevated" color="primary" class="ms-2" prepend-icon="$pencil" @click="editRequestInfo">
                                    Edit
                                </v-btn>
                                <v-btn v-if="isEditingRequest" variant="elevated" @click="didFinishEditingRequestInfo">
                                    Cancel
                                </v-btn>
                                <v-btn v-if="isEditingRequest" variant="elevated" color="primary" class="ms-2" :disabled="!requestFormValid" @click="updateRequestInfo">
                                    Save
                                </v-btn>
                                <v-btn variant="elevated" color="error" class="ms-2" disabled>
                                    Submit
                                </v-btn>
                            </v-row>
                        </v-card-title>
                        <v-card-text v-if="!isEditingRequest" class="text-body-1">
                            <strong>Request Protocol:</strong> {{ request.protocol?.name ?? "None" }}<br>
                            <strong>Material Samples Container Type:</strong> {{ request.samplesContainerType?.name ?? "Not configured" }}<br>
                            <strong>Request Processing Container Type:</strong> {{ request.requestContainerType?.name ?? "Not configured" }}<br>
                            <strong>Requester:</strong> {{ request.requestedByEmail }}<br>
                            <strong>Processor:</strong> {{ request.processor?.name ?? "None"}}<br>
                            <strong>Process By:</strong> {{ formattedRequestNeedByDate }}<br>
                            <strong>Status:</strong> {{ _.capitalize(request.status) }}<br>
                        </v-card-text>
                        <v-card-text v-else-if="editedRequest">
                            <RequestForm
                                ref="requestForm"
                                v-model="editedRequest"
                                v-model:valid="requestFormValid"
                                :embedded="true"
                                :protocols="requestProtocolOptions"
                                :processors="requestProcessorOptions"
                                :container-types="containerTypeOptions"
                                @update:model-value="onEditedRequestChanged"
                            />
                        </v-card-text>
                    </v-card>
                </v-card-text>
                <v-card-text>
                    <v-card>
                        <v-card-title>
                            <v-row no-gutters>
                                Material Samples Containers
                                <v-spacer/>
                                <v-btn variant="elevated" color="primary" @click="addMaterialSampleContainer" disabled>Add Container</v-btn>
                            </v-row>
                        </v-card-title>
                        <v-card-text class="text-body-1">
                            Not configured
                        </v-card-text>
                    </v-card>
                </v-card-text>
                <v-card-text>
                    <v-card>
                        <v-card-title>
                            <v-row no-gutters>
                                Request Processing Containers
                                <v-spacer/>
                                <v-btn variant="elevated" color="primary" @click="addRequestProcessingContainer" disabled>Add Container</v-btn>
                            </v-row>
                        </v-card-title>
                        <v-card-text class="text-body-1">
                            Not configured
                        </v-card-text>
                    </v-card>
                </v-card-text>
            </v-card>
        </v-card-text>
    </v-card>
</template>
<script setup lang="ts">
import { useRoute } from 'vue-router';
import { onMounted, ref, computed } from "vue";
import { Request, SavedRequest, RequestProcessor, samplesAPI, SavedRequestProtocol } from "@/services/samples/samplesAPI";
import { withLoadingScreen } from "@/lib/loading";
import _ from "lodash";
import { ContainerType } from "@/services/commonModels";
import RequestForm from "@/components/SamplesV2/RequestForm.vue";

const route = useRoute();
const requestCode = route.params.requestCode as string;

const tab = ref();
const request = ref<SavedRequest | null>(null);

const editedRequest = ref<Request | null>(null);
const isEditingRequest = ref<boolean>(false);
const requestFormValid = ref(false);
const requestForm = ref();

// Dropdown options for form
const requestProtocolOptions = ref<SavedRequestProtocol[]>([]);
const requestProcessorOptions = ref<RequestProcessor[]>([]);
const containerTypeOptions = ref<ContainerType[]>([]);

onMounted(() => {
   fetchRequestData();
});

const formattedRequestNeedByDate = computed(() => {
    if (request.value?.needByDate) {
        const date = new Date(request.value?.needByDate);
        return date.toLocaleDateString();
    }
    return "N/A";
});

// Functions
async function fetchRequestData() {
    await withLoadingScreen(async () => {
        requestProtocolOptions.value = await samplesAPI.fetchRequestProtocols();
        requestProcessorOptions.value = await samplesAPI.fetchRequestProcessors();
        containerTypeOptions.value = await samplesAPI.fetchRequestContainerTypes();
        request.value = await samplesAPI.fetchRequestByRequestCode(requestCode);
    })
}

function editRequestInfo() {
    editedRequest.value = request.value;
    isEditingRequest.value = true;
}

async function updateRequestInfo(): Promise<void> {
    await withLoadingScreen(async () => {
        await requestForm.value.saveRequest();
        didFinishEditingRequestInfo();
        await fetchRequestData();
    });
}

function didFinishEditingRequestInfo() {
    isEditingRequest.value = false;
    editedRequest.value = null;
}

function onEditedRequestChanged(updatedRequest: Request): void {
    editedRequest.value = updatedRequest;
}

function addMaterialSampleContainer() {
    console.log("Add material sample container - not implemented yet!");
}

function addRequestProcessingContainer() {
    console.log("Add request processing container - not implemented yet!");
}

</script>
